import { motion } from "framer-motion";
import { STYLES, STYLES_EN } from "@/lib/data";
import { STRINGS } from "@/lib/i18n";
import { useLanguage } from "@/context/LanguageContext";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Styles() {
  const { lang } = useLanguage();
  const t = STRINGS[lang].stylesSection;
  const styles = lang === "en" ? STYLES_EN : STYLES;

  return (
    <section id="estilos" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 lg:py-36" data-testid="styles-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="mb-16 flex flex-wrap items-end justify-between gap-6"
      >
        <div className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{t.overline}</p>
          <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
            {t.title[0]}
            <em className="italic text-blue-400">{t.title[1]}</em>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-slate-400">{t.copy}</p>
      </motion.div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {styles.map((style, i) => (
          <motion.article
            key={style.id}
            custom={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={cardVariants}
            className="group overflow-hidden rounded-2xl border border-white/10 bg-[#0B101C] transition-shadow duration-500 hover:shadow-[0_0_40px_rgba(59,130,246,0.18)]"
            data-testid={`style-card-${style.id}`}
          >
            <div className="relative h-44 overflow-hidden">
              <img
                src={style.image}
                alt={`${style.name} guitar`}
                className="h-full w-full object-cover grayscale transition-[transform,filter] duration-700 ease-out group-hover:scale-105 group-hover:grayscale-0"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B101C] to-transparent" aria-hidden="true" />
            </div>
            <div className="p-6">
              <h3 className="font-serif text-2xl text-blue-50">{style.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{style.desc}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {style.concepts.map((concept) => (
                  <span
                    key={concept}
                    className="rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400"
                  >
                    {concept}
                  </span>
                ))}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
}
